import React, { useId } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import type { UseAtAGlanceResult } from '../../at-a-glance/useAtAGlance.ts';
import { TeamworkGraphAskRovoButton } from '../TeamworkGraphAskRovoButton.tsx';

const LOADING_BAR_WIDTHS = ['100%', '85%', '70%'] as const;

const styles = cssMap({
	card: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	body: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
	},
	loading: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
});

const i18n = defineMessages({
	contextFromYourWork: {
		id: 'byline-connections.preview-panel.section.context-from-your-work',
		defaultMessage: 'Context from your work',
		description:
			'Heading of the Teamwork Graph section that summarises this page and its connections in a paragraph.',
	},
	loading: {
		id: 'byline-connections.preview-panel.section.context-from-your-work.loading',
		defaultMessage: 'Loading context from your work',
		description:
			'Accessible label for the placeholder bars shown in the Context from your work section while the overview paragraph is being generated.',
	},
	error: {
		id: 'byline-connections.preview-panel.section.at-a-glance.error',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "We couldn't generate this summary",
		description:
			'Shown in the Context from your work section when generating the overview paragraph fails. Tells the user the summary is unavailable without exposing the technical error.',
	},
	retryWaiting: {
		id: 'byline-connections.preview-panel.section.at-a-glance.retry-waiting',
		defaultMessage: 'The summary service is busy. We’ll try again shortly.',
		description: 'Shown while waiting to retry a summary request after a temporary rate limit.',
	},
	insufficientEvidence: {
		id: 'byline-connections.preview-panel.section.at-a-glance.insufficient-evidence.ai-non-final',
		defaultMessage:
			'We couldn’t generate summaries because there isn’t enough information in this page and its connections.',
		description:
			'Shown in the Context from your work section whenever there is no summary to show and nothing failed: the page and its connections do not contain enough information to generate useful summaries, or there was too little to send to the summaries service at all.',
	},
});

const Loading = ({ waitingForQuota }: { waitingForQuota: boolean }): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box
			xcss={styles.loading}
			role="status"
			aria-label={formatMessage(i18n.loading)}
			testId="teamwork-graph-preview-panel-at-a-glance-section--loading"
		>
			{LOADING_BAR_WIDTHS.map((width) => (
				<Skeleton
					key={width}
					width={width}
					height={token('space.200')}
					borderRadius={token('radius.small')}
					isShimmering
				/>
			))}
			{waitingForQuota ? (
				<Text as="p" color="color.text.subtle">
					{formatMessage(i18n.retryWaiting)}
				</Text>
			) : null}
		</Box>
	);
};

/** Older responses may contain multiple items, so the whole overview joins into one paragraph. */
const Summary = ({
	overview,
}: {
	overview: NonNullable<UseAtAGlanceResult['output']>['overview'];
}): React.JSX.Element => (
	<Text as="p" color="color.text.subtle">
		{overview.map((item) => item.text).join(' ')}
	</Text>
);

const Failed = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box role="alert" testId="teamwork-graph-preview-panel-at-a-glance-section--error">
			<Text as="p" color="color.text.subtle">
				{formatMessage(i18n.error)}
			</Text>
		</Box>
	);
};

const InsufficientEvidence = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box testId="teamwork-graph-preview-panel-at-a-glance-section--insufficient-evidence">
			<Text as="p" color="color.text.subtle">
				{formatMessage(i18n.insufficientEvidence)}
			</Text>
		</Box>
	);
};

export type AtAGlanceSectionProps = UseAtAGlanceResult & {
	/**
	 * Opens Rovo chat. When passed, the section ends with the "Chat with Rovo" button; when
	 * omitted (no Rovo on the site), no button renders.
	 */
	onAskRovo?: () => void;
};

/**
 * The always-expanded "Context from your work" card, shared by the panel and modal.
 * Per-link one-liners belong to Related content rows. The card renders in every
 * state — loading, summary, failure, and no summary at all — so readers always find
 * the section where they left it. The Rovo action stays available in all of them.
 */
export const AtAGlanceSection = ({
	output,
	loading,
	error,
	retryPending,
	retryReason,
	onAskRovo,
}: AtAGlanceSectionProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const headingId = useId();
	const overview = output?.overview;
	// An unavailable response is a generation failure, so it shares the failure copy. Every
	// other summary-less state — no request made, insufficient evidence, or an empty
	// generated overview — is a lack of material to summarise rather than an error.
	const failed = Boolean(error) || output?.status === 'UNAVAILABLE';

	let body: React.JSX.Element;
	if ((loading || retryPending) && !overview?.length) {
		body = <Loading waitingForQuota={Boolean(retryPending && retryReason === 'rateLimited')} />;
	} else if (overview?.length) {
		// Keep the summary if a later refetch fails.
		body = <Summary overview={overview} />;
	} else if (failed) {
		body = <Failed />;
	} else {
		body = <InsufficientEvidence />;
	}

	return (
		<Box
			xcss={styles.card}
			role="region"
			aria-labelledby={headingId}
			testId="teamwork-graph-preview-panel-at-a-glance-section"
		>
			<Heading size="xsmall" as="h3" id={headingId}>
				<Text color="color.text.subtle" weight="bold">
					{formatMessage(i18n.contextFromYourWork)}
				</Text>
			</Heading>
			<Box xcss={styles.body} testId="teamwork-graph-preview-panel-at-a-glance-section--body">
				{body}
				{onAskRovo ? (
					<Box testId="teamwork-graph-preview-panel-at-a-glance-section--ask-rovo-slot">
						<TeamworkGraphAskRovoButton
							onClick={onAskRovo}
							testId="teamwork-graph-preview-panel-at-a-glance-section--ask-rovo"
						/>
					</Box>
				) : null}
			</Box>
		</Box>
	);
};
